import React, { useEffect, useState } from 'react';
import { getImageURL } from '../../utils/api';
import axiosInstance from '../../utils/axiosInstance';
import DashboardLayout from '../../components/DashboardLayout';
import Swal from 'sweetalert2';

function to12h(time24) {
    if (!time24) return '';
    const [h, m] = time24.split(':');
    let hours = parseInt(h, 10);
    const modifier = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12 || 12;
    return `${hours}:${m} ${modifier}`;
}

// Build a full image URL using the shared API helper, handling relative paths
const buildImageSrc = (imagePath) => {
    if (!imagePath) return null;
    const trimmed = String(imagePath).trim();
    if (!trimmed) return null;
    // If it's already an absolute URL, return as-is
    if (/^https?:\/\//i.test(trimmed)) return trimmed;

    // Ensure it starts with a single leading slash
    const normalized = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;

    // Avoid double "/api/api" when backend already prefixes paths with /api
    const baseEndsWithApi = getImageURL('')[getImageURL('').length - 1] === 'i' && getImageURL('').endsWith('/api');
    const cleaned = baseEndsWithApi && normalized.startsWith('/api/')
        ? normalized.slice(4)
        : normalized;

    return getImageURL(cleaned);
};

// Remove duplicate schedule objects based on key fields, preferring ones
// where attendance is already marked
const deduplicateSchedules = (list) => {
    if (!Array.isArray(list) || list.length === 0) return [];

    const normalize = (value) => String(value ?? '').trim().toLowerCase();

    const map = new Map();

    for (const s of list) {
        const batchGroupName =
            s.batchGroupName ||
            s.batchGroupWhatsappName ||
            s.whatsappGroupName ||
            s.batchInternalName ||
            '';

        const key = [
            normalize(batchGroupName),
            normalize(s.subjectShortName || s.subjectFullName),
            normalize(s.startTime),
            normalize(s.endTime),
            normalize(s.staffName),
            normalize(s.classroomName),
        ].join('|');

        const existing = map.get(key);
        if (!existing) {
            map.set(key, s);
            continue;
        }

        const existingMarked =
            existing.isAttendanceMarked === true ||
            existing.isAttendanceMarked === 'true';
        const currentMarked =
            s.isAttendanceMarked === true ||
            s.isAttendanceMarked === 'true';

        // If the existing one is unmarked but the new one is marked,
        // replace it. Otherwise keep the existing entry.
        if (!existingMarked && currentMarked) {
            map.set(key, s);
        }
    }

    return Array.from(map.values());
};

export default function AttendanceTracking() {
    const [coordinateCourses, setCoordinateCourses] = useState({});
    const [accessibleCourseIds, setAccessibleCourseIds] = useState([]);
    const [isSuperAdmin, setIsSuperAdmin] = useState(false);

    const [selectedDate, setSelectedDate] = useState(() => {
        const today = new Date();
        const y = today.getFullYear();
        const m = String(today.getMonth() + 1).padStart(2, '0');
        const d = String(today.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
    });
    const [schedules, setSchedules] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [modalLoading, setModalLoading] = useState(false);
    const [attendanceDetails, setAttendanceDetails] = useState(null);
    const [selectedSchedule, setSelectedSchedule] = useState(null);
    const [studentImages, setStudentImages] = useState({});
    const [loadingImages, setLoadingImages] = useState(new Set());
    const [lecturerImage, setLecturerImage] = useState('');
    const [lecturerImageLoading, setLecturerImageLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCourseLabels, setSelectedCourseLabels] = useState(new Set());

    // Helpers for course labeling and normalization
    const normalizeLoose = (str) =>
        String(str || '')
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, ' ')
            .trim()
            .replace(/\s+/g, ' ');

    const isHndComputing = (v) => {
        const n = normalizeLoose(v);
        return n.includes('hnd in computing') || (n.includes('hnd') && n.includes('comput'));
    };

    const getCourseLabel = (s) => {
        const name = (s.courseName || '').toString();
        const short = (s.courseShortName || '').toString();
        if (isHndComputing(name) || isHndComputing(short)) return 'HND in Computing';
        return (short || name || 'Unknown Course').toString().trim();
    };

    // Load coordinate courses / access control once on mount
    useEffect(() => {
        let isMounted = true;
        (async () => {
            try {
                const accessRes = await axiosInstance.get('/admin/me/coordinate-courses').catch(() => ({ data: {} }));
                if (!isMounted) return;

                const data = accessRes.data || {};
                setCoordinateCourses(data.coordinateCourses || {});
                setAccessibleCourseIds(Array.isArray(data.accessibleCourseIds) ? data.accessibleCourseIds : []);
                setIsSuperAdmin(!!data.isSuperAdmin);
            } catch (e) {
                console.error('Failed to load coordinate courses', e);
            }
        })();
        return () => {
            isMounted = false;
        };
    }, []);

    // Allowed course IDs for coordinators (derived from backend access info)
    const allowedCourseIdsSet = React.useMemo(() => {
        const ids = Array.isArray(accessibleCourseIds) ? accessibleCourseIds : [];
        const set = new Set();
        ids.forEach((id) => {
            const num = Number(id);
            if (Number.isFinite(num)) set.add(num);
        });
        return set;
    }, [accessibleCourseIds]);

    useEffect(() => {
        if (selectedDate) {
            fetchSchedules(selectedDate);
        }
    }, [selectedDate]);

    const fetchSchedules = async (date) => {
        setLoading(true);
        try {
            const response = await axiosInstance.get('/admin/attendance/schedules', {
                params: { date },
                // Treat 304 (Not Modified) as a successful response so axios doesn't throw
                validateStatus: (status) => (status >= 200 && status < 300) || status === 304,
            });

            if (response.status === 304) {
                // No new data; keep existing schedules
                return;
            }

            const raw = Array.isArray(response.data) ? response.data : [];
            const deduped = deduplicateSchedules(raw);
            setSchedules(deduped);
        } catch (error) {
            console.error('Error fetching schedules:', error);
            Swal.fire({
                title: 'Error',
                text: 'Failed to load schedules',
                icon: 'error',
            });
        } finally {
            setLoading(false);
        }
    };

    const fetchAttendanceDetails = async (scheduleId, schedule) => {
        console.log('Fetching attendance details for schedule ID:', scheduleId);
        setModalLoading(true);
        setSelectedSchedule(schedule);
        setShowModal(true);
        
        try {
            const response = await axiosInstance.get(`/admin/attendance/details/${scheduleId}`);
            const data = response.data;
            setAttendanceDetails(data);
        } catch (error) {
            console.error('Error fetching attendance details:', error);
            Swal.fire({
                title: 'Error',
                text: error instanceof Error ? error.message : 'Failed to load attendance details',
                icon: 'error',
            });
            setShowModal(false);
        } finally {
            setModalLoading(false);
        }
    };

    const closeModal = () => {
        setShowModal(false);
        setAttendanceDetails(null);
        setSelectedSchedule(null);
        setSearchQuery(''); // Reset search when closing modal
    };

    // Filter students based on search query
    const filterStudents = (students) => {
        if (!searchQuery.trim()) {
            return students;
        }
        
        const query = searchQuery.toLowerCase().trim();
        return students.filter((student) => {
            const studentId = (student.studentId || '').toString().toLowerCase();
            const studentName = (student.studentName || '').toLowerCase();
            const registrationNumber = (student.registrationNumber || '').toString().toLowerCase();
            
            return studentId.includes(query) || 
                   studentName.includes(query) || 
                   registrationNumber.includes(query);
        });
    };

    const loadStudentImage = (studentId, imagePath) => {
        if (!imagePath || studentImages[studentId] || loadingImages.has(studentId)) {
            return;
        }

        setLoadingImages(prev => new Set(prev).add(studentId));

        try {
            const resolved = buildImageSrc(imagePath);
            if (resolved) {
                setStudentImages(prev => ({ ...prev, [studentId]: resolved }));
            }
        } catch (error) {
            console.error('Error loading student image:', error);
        } finally {
            setLoadingImages(prev => {
                const newSet = new Set(prev);
                newSet.delete(studentId);
                return newSet;
            });
        }
    };

    useEffect(() => {
        if (attendanceDetails) {
            // Load images for all students
            const allStudents = [
                ...(attendanceDetails.present || []),
                ...(attendanceDetails.absent || []),
                ...(attendanceDetails.late || []),
                ...(attendanceDetails.excused || [])
            ];

            allStudents.forEach(student => {
                if (student.studentImage) {
                    loadStudentImage(student.studentId, student.studentImage);
                }
            });
        }
    }, [attendanceDetails]);

    const loadLecturerImage = (imagePath) => {
        if (!imagePath || lecturerImage) {
            return;
        }

        setLecturerImageLoading(true);

        try {
            const resolved = buildImageSrc(imagePath);
            if (resolved) {
                setLecturerImage(resolved);
            }
        } catch (error) {
            console.error('Error loading lecturer image:', error);
        } finally {
            setLecturerImageLoading(false);
        }
    };

    useEffect(() => {
        if (selectedSchedule?.staffImagePath) {
            loadLecturerImage(selectedSchedule.staffImagePath);
        } else {
            setLecturerImage('');
        }
    }, [selectedSchedule]);


    const toggleAttendanceStatus = async (attendanceId, currentStatus) => {
        const newStatus = currentStatus === 'Present' ? 'Absent' : 'Present';
        
        try {
            const response = await axiosInstance.post('/admin/attendance/toggle-status', {
                attendanceId,
                status: newStatus,
            });

            const data = response.data;
            if (!response || response.status !== 200) {
                throw new Error((data && (data.message || data.error)) || 'Failed to toggle status');
            }

            // Refresh the attendance details
            if (selectedSchedule) {
                await fetchAttendanceDetails(selectedSchedule.scheduleId, selectedSchedule);
                // Also refresh the schedules list to update counts
                await fetchSchedules(selectedDate);
            }

            Swal.fire({
                title: 'Success',
                text: `Attendance status changed to ${newStatus}`,
                icon: 'success',
                timer: 2000,
                showConfirmButton: false
            });
        } catch (error) {
            console.error('Error toggling attendance status:', error);
            Swal.fire({
                title: 'Error',
                text: error instanceof Error ? error.message : 'Failed to toggle attendance status',
                icon: 'error',
            });
        }
    };

    const handleMarkAllAbsent = async (schedule) => {
        // Show confirmation dialog
        const result = await Swal.fire({
            title: 'Mark All Students as Absent?',
            html: `
                <div class="text-left">
                    <p class="mb-3">You are about to mark all students as <strong class="text-red-600">Absent</strong> for:</p>
                    <div class="bg-gray-50 rounded-lg p-4 mb-3">
                        <p class="font-semibold text-gray-900">${schedule.subjectFullName || schedule.subjectShortName}</p>
                        <p class="text-sm text-gray-600 mt-1">
                            ${schedule.batchGroupId 
                                ? `<span class="inline-flex items-center rounded-full bg-indigo-100 px-2 py-0.5 text-xs font-medium text-indigo-800 mr-1">Group</span>${schedule.batchGroupWhatsappName || schedule.batchGroupName || 'Batch Group'}`
                                : (schedule.whatsappGroupName || schedule.batchInternalName || 'Batch')
                            }
                        </p>
                        <p class="text-sm text-gray-600">
                            ${to12h(schedule.startTime)} - ${to12h(schedule.endTime)}
                        </p>
                    </div>
                    <p class="text-sm text-gray-600">
                        This action will create attendance records for all students${schedule.batchGroupId ? ' from all batches in this group' : ' in this batch'} with status <strong>Absent</strong>.
                    </p>
                </div>
            `,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc2626',
            cancelButtonColor: '#6b7280',
            confirmButtonText: 'Yes, Mark as Absent',
            cancelButtonText: 'Cancel',
            customClass: {
                popup: 'text-left'
            }
        });

        if (!result.isConfirmed) {
            return;
        }

        try {
            const response = await axiosInstance.post('/admin/attendance/mark-all-absent', {
                scheduleId: schedule.scheduleId,
            });

            const data = response.data;
            if (!response || response.status !== 200) {
                throw new Error((data && (data.message || data.error)) || 'Failed to mark students as absent');
            }

            // Refresh the schedules list to show updated status
            await fetchSchedules(selectedDate);

            Swal.fire({
                title: 'Success!',
                html: `
                    <p>Successfully marked <strong>${data.studentsMarked}</strong> student(s) as absent.</p>
                `,
                icon: 'success',
                timer: 3000,
                showConfirmButton: false
            });
        } catch (error) {
            console.error('Error marking students as absent:', error);
            Swal.fire({
                title: 'Error',
                text: error instanceof Error ? error.message : 'Failed to mark students as absent',
                icon: 'error',
            });
        }
    };

    const isSchedulePast = (schedule) => {
        const now = new Date();
        const scheduleDateTime = new Date(`${schedule.date}T${schedule.endTime}`);
        return scheduleDateTime < now;
    };

    // Build base schedules considering access permissions
    const baseSchedules = React.useMemo(() => {
        // Super admins can see all courses / batches
        if (isSuperAdmin) return schedules;

        // Coordinators see only schedules whose courseId is in their accessibleCourseIds
        return schedules.filter((s) => {
            if (!s.courseId) return false;
            return allowedCourseIdsSet.has(Number(s.courseId));
        });
    }, [schedules, allowedCourseIdsSet, isSuperAdmin]);

    // Available course options (from baseSchedules only)
    const availableCourseOptions = React.useMemo(() => {
        const map = new Map();
        for (const s of baseSchedules) {
            const label = getCourseLabel(s);
            map.set(label, (map.get(label) || 0) + 1);
        }
        return Array.from(map.entries())
            .sort((a, b) => a[0].localeCompare(b[0]))
            .map(([label, count]) => ({ label, count }));
    }, [baseSchedules]);

    // Ensure selection stays within available options when date or access changes
    useEffect(() => {
        if (selectedCourseLabels.size === 0) return;
        const allowed = new Set(availableCourseOptions.map(o => o.label));
        let changed = false;
        const next = new Set();
        selectedCourseLabels.forEach((l) => {
            if (allowed.has(l)) next.add(l); else changed = true;
        });
        if (changed) setSelectedCourseLabels(next);
    }, [availableCourseOptions]);

    // Apply course selection filter
    const filteredSchedules = React.useMemo(() => {
        if (selectedCourseLabels.size === 0) return baseSchedules;
        return baseSchedules.filter((s) => selectedCourseLabels.has(getCourseLabel(s)));
    }, [baseSchedules, selectedCourseLabels]);

    const groupSchedulesByCourse = (rows) => {
        const grouped = {};
        const seenSchedules = new Set();

        rows.forEach((schedule) => {
            const courseKey = getCourseLabel(schedule);
            
            // Create a unique key based on all identifying attributes
            const uniqueKey = [
                schedule.subjectFullName || schedule.subjectShortName,
                schedule.startTime,
                schedule.endTime,
                schedule.classroomName,
                schedule.staffName,
                schedule.batchGroupId 
                    ? `group-${schedule.batchGroupId}` 
                    : `batch-${schedule.batchId}`,
                schedule.date
            ].join('|');
            
            // Skip if we've already seen this exact combination
            if (seenSchedules.has(uniqueKey)) {
                return;
            }
            seenSchedules.add(uniqueKey);
            
            if (!grouped[courseKey]) grouped[courseKey] = [];
            grouped[courseKey].push(schedule);
        });

        return grouped;
    };
    const groupedSchedules = groupSchedulesByCourse(filteredSchedules);

    return (
        <DashboardLayout title="Attendance Tracking">

            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
                {/* Header */}
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-gray-900">Attendance Tracking</h1>
                    <p className="mt-2 text-sm text-gray-600">
                        View scheduled classes and check attendance status
                    </p>
                </div>

                {/* Date + Course Filters */}
                <div className="mb-6 rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
                    {/* Date selector */}
                    <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2">
                            <svg
                                className="h-5 w-5 text-gray-500"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                                />
                            </svg>
                            <label htmlFor="date" className="text-sm font-semibold text-gray-700">
                                Select Date
                            </label>
                        </div>
                        <input
                            type="date"
                            id="date"
                            value={selectedDate}
                            onChange={(e) => setSelectedDate(e.target.value)}
                            className="rounded-md border-gray-300 px-4 py-2 text-sm shadow-sm transition-colors focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-0"
                        />
                    </div>

                    {/* Course multi-select (only shows coordinated courses for non-admins) */}
                    {availableCourseOptions.length > 0 && (
                        <div className="mt-6">
                            <div className="mb-2 flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <svg className="h-5 w-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7h18M3 12h18M3 17h18" />
                                    </svg>
                                    <span className="text-sm font-semibold text-gray-700">Filter by Course</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <button
                                        type="button"
                                        onClick={() => setSelectedCourseLabels(new Set(availableCourseOptions.map(o => o.label)))}
                                        className="text-xs px-2 py-1 rounded-md bg-gray-100 hover:bg-gray-200 text-gray-700"
                                    >
                                        Select All
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setSelectedCourseLabels(new Set())}
                                        className="text-xs px-2 py-1 rounded-md bg-gray-100 hover:bg-gray-200 text-gray-700"
                                    >
                                        Clear
                                    </button>
                                </div>
                            </div>

                            <div className="flex flex-wrap gap-2">
                                {availableCourseOptions.map(({ label, count }) => {
                                    const selected = selectedCourseLabels.has(label);
                                    return (
                                        <button
                                            key={label}
                                            type="button"
                                            onClick={() => {
                                                const next = new Set(selectedCourseLabels);
                                                if (next.has(label)) next.delete(label); else next.add(label);
                                                setSelectedCourseLabels(next);
                                            }}
                                            className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${selected ? 'border-indigo-500 bg-indigo-50 text-indigo-700' : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'}`}
                                            title={`${label} (${count} classes)`}
                                        >
                                            <span>{label}</span>
                                            <span className={`inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full px-1 ${selected ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100 text-gray-700'}`}>{count}</span>
                                        </button>
                                    );
                                })}
                            </div>
                            {selectedCourseLabels.size > 0 && (
                                <div className="mt-2 text-xs text-gray-600">
                                    Showing selected {selectedCourseLabels.size} course{selectedCourseLabels.size > 1 ? 's' : ''}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Loading State */}
                {loading && (
                    <div className="flex items-center justify-center py-12">
                        <div className="h-12 w-12 animate-spin rounded-full border-4 border-indigo-600 border-t-transparent"></div>
                    </div>
                )}

                {/* Schedules Display */}
                {!loading && baseSchedules.length === 0 && (
                    <div className="rounded-lg bg-gray-50 p-8 text-center">
                        <svg
                            className="mx-auto h-12 w-12 text-gray-400"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                            />
                        </svg>
                        <p className="mt-4 text-sm text-gray-600">No schedules found for this date in your coordinated courses</p>
                    </div>
                )}

                {/* Schedules Grouped by Course */}
                {!loading && baseSchedules.length > 0 && (
                    <div className="space-y-8">
                        {Object.keys(groupedSchedules).length === 0 && (
                            <div className="rounded-lg bg-gray-50 p-8 text-center text-sm text-gray-600">
                                No schedules match the selected course filters
                            </div>
                        )}
                        {Object.entries(groupedSchedules).map(([courseName, courseSchedules]) => {
                            const markedCount = courseSchedules.filter(s => s.isAttendanceMarked).length;
                            const notMarkedCount = courseSchedules.length - markedCount;
                            
                            return (
                                <div key={courseName}>
                                    <div className="mb-4">
                                        <h2 className="text-xl font-semibold text-gray-900">{courseName}</h2>
                                        <div className="mt-2 flex items-center gap-4 text-sm">
                                            <div className="flex items-center gap-2">
                                                <span className="inline-flex h-3 w-3 rounded-full bg-green-500"></span>
                                                <span className="text-gray-600">
                                                    <span className="font-semibold text-gray-900">{markedCount}</span> Marked
                                                </span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <span className="inline-flex h-3 w-3 rounded-full bg-red-500"></span>
                                                <span className="text-gray-600">
                                                    <span className="font-semibold text-gray-900">{notMarkedCount}</span> Not Marked
                                                </span>
                                            </div>
                                            <div className="text-gray-500">
                                                • Total: <span className="font-semibold text-gray-900">{courseSchedules.length}</span> Classes
                                            </div>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                                        {courseSchedules.map((schedule) => (
                                        <div
                                            key={schedule.scheduleId}
                                            className="relative overflow-hidden rounded-lg border border-gray-200 bg-white p-6 shadow-sm transition-all hover:shadow-md"
                                        >
                                            {/* Attendance Status Badge */}
                                            <div className="absolute right-4 top-4">
                                                {schedule.isAttendanceMarked ? (
                                                    <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-3 py-1 text-xs font-medium text-green-800">
                                                        <svg
                                                            className="h-4 w-4"
                                                            fill="currentColor"
                                                            viewBox="0 0 20 20"
                                                        >
                                                            <path
                                                                fillRule="evenodd"
                                                                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                                                clipRule="evenodd"
                                                            />
                                                        </svg>
                                                        Marked
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-3 py-1 text-xs font-medium text-red-800">
                                                        <svg
                                                            className="h-4 w-4"
                                                            fill="currentColor"
                                                            viewBox="0 0 20 20"
                                                        >
                                                            <path
                                                                fillRule="evenodd"
                                                                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                                                                clipRule="evenodd"
                                                            />
                                                        </svg>
                                                        Not Marked
                                                    </span>
                                                )}
                                            </div>

                                            {/* Subject Info */}
                                            <div className="mb-4 pr-24">
                                                <h3 className="text-lg font-semibold text-gray-900">
                                                    {schedule.subjectShortName || schedule.subjectFullName}
                                                </h3>
                                                {schedule.subjectShortName && schedule.subjectFullName && (
                                                    <p className="mt-1 text-sm text-gray-500">
                                                        {schedule.subjectFullName}
                                                    </p>
                                                )}
                                            </div>

                                            {/* Time */}
                                            <div className="mb-3 flex items-center gap-2 text-sm text-gray-600">
                                                <svg
                                                    className="h-5 w-5 text-gray-400"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    viewBox="0 0 24 24"
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth={2}
                                                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                                                    />
                                                </svg>
                                                <span>
                                                    {to12h(schedule.startTime)} - {to12h(schedule.endTime)}
                                                </span>
                                            </div>

                                            {/* Classroom */}
                                            {schedule.classroomName && (
                                                <div className="mb-3 flex items-center gap-2 text-sm text-gray-600">
                                                    <svg
                                                        className="h-5 w-5 text-gray-400"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        viewBox="0 0 24 24"
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth={2}
                                                            d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                                                        />
                                                    </svg>
                                                    <span>{schedule.classroomName}</span>
                                                </div>
                                            )}

                                            {/* Lecturer */}
                                            {schedule.staffName && (
                                                <div className="mb-3 flex items-center gap-2 text-sm text-gray-600">
                                                    <svg
                                                        className="h-5 w-5 text-gray-400"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        viewBox="0 0 24 24"
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth={2}
                                                            d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                                                        />
                                                    </svg>
                                                    <span>{schedule.staffName}</span>
                                                </div>
                                            )}

                                            {/* Batch */}
                                            {(schedule.batchGroupId ? (schedule.batchGroupWhatsappName || schedule.batchGroupName) : (schedule.batchInternalName || schedule.whatsappGroupName)) && (
                                                <div className="flex items-center gap-2 text-sm text-gray-600">
                                                    <svg
                                                        className="h-5 w-5 text-gray-400"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        viewBox="0 0 24 24"
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth={2}
                                                            d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                                                        />
                                                    </svg>
                                                    <span>
                                                        {schedule.batchGroupId 
                                                            ? (schedule.batchGroupWhatsappName || schedule.batchGroupName)
                                                            : (schedule.whatsappGroupName || schedule.batchInternalName)
                                                        }
                                                        {schedule.batchGroupId && (
                                                            <span className="ml-1 inline-flex items-center rounded-full bg-indigo-100 px-2 py-0.5 text-xs font-medium text-indigo-800">
                                                                Group
                                                            </span>
                                                        )}
                                                    </span>
                                                </div>
                                            )}

                                            {/* Attendance Summary */}
                                            {schedule.isAttendanceMarked && (
                                                <div className="mt-4 border-t border-gray-200 pt-4">
                                                    <div className="mb-3">
                                                        <div className="mb-2 flex items-center justify-between text-sm">
                                                            <span className="font-medium text-gray-700">
                                                                Attendance
                                                            </span>
                                                            <span className="font-semibold text-gray-900">
                                                                {schedule.attendancePercentage}%
                                                            </span>
                                                        </div>
                                                        {/* Progress Bar */}
                                                        <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200">
                                                            <div
                                                                className={`h-full rounded-full transition-all ${
                                                                    schedule.attendancePercentage >= 75
                                                                        ? 'bg-green-500'
                                                                        : schedule.attendancePercentage >= 50
                                                                        ? 'bg-yellow-500'
                                                                        : 'bg-red-500'
                                                                }`}
                                                                style={{ width: `${schedule.attendancePercentage}%` }}
                                                            ></div>
                                                        </div>
                                                    </div>
                                                    
                                                    {/* Attendance Stats */}
                                                    <div className="grid grid-cols-2 gap-2 text-xs">
                                                        <div className="flex items-center justify-between rounded-md bg-green-50 px-2 py-1.5">
                                                            <span className="text-green-700">Present</span>
                                                            <span className="font-semibold text-green-900">
                                                                {schedule.presentCount}
                                                            </span>
                                                        </div>
                                                        <div className="flex items-center justify-between rounded-md bg-red-50 px-2 py-1.5">
                                                            <span className="text-red-700">Absent</span>
                                                            <span className="font-semibold text-red-900">
                                                                {schedule.absentCount}
                                                            </span>
                                                        </div>
                                                        {schedule.lateCount > 0 && (
                                                            <div className="flex items-center justify-between rounded-md bg-yellow-50 px-2 py-1.5">
                                                                <span className="text-yellow-700">Late</span>
                                                                <span className="font-semibold text-yellow-900">
                                                                    {schedule.lateCount}
                                                                </span>
                                                            </div>
                                                        )}
                                                        {schedule.excusedCount > 0 && (
                                                            <div className="flex items-center justify-between rounded-md bg-blue-50 px-2 py-1.5">
                                                                <span className="text-blue-700">Excused</span>
                                                                <span className="font-semibold text-blue-900">
                                                                    {schedule.excusedCount}
                                                                </span>
                                                            </div>
                                                        )}
                                                        <div className="flex items-center justify-between rounded-md bg-gray-100 px-2 py-1.5">
                                                            <span className="text-gray-700">Total</span>
                                                            <span className="font-semibold text-gray-900">
                                                                {schedule.totalStudents}
                                                            </span>
                                                        </div>
                                                    </div>
                                                    
                                                    {/* Show Summary Button */}
                                                    <button
                                                        onClick={() => fetchAttendanceDetails(schedule.scheduleId, schedule)}
                                                        className="mt-3 w-full rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                                                    >
                                                        Show Summary
                                                    </button>
                                                </div>
                                            )}

                                            {/* Mark Absent Button for past schedules that are not marked */}
                                            {!schedule.isAttendanceMarked && isSchedulePast(schedule) && (
                                                <div className="mt-4 border-t border-gray-200 pt-4">
                                                    <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 mb-3">
                                                        <div className="flex items-start gap-2">
                                                            <svg className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                                                                <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                                                            </svg>
                                                            <div className="flex-1">
                                                                <p className="text-sm font-medium text-amber-800">
                                                                    Schedule has passed
                                                                </p>
                                                                <p className="text-xs text-amber-700 mt-1">
                                                                    Attendance was not marked for this session
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <button
                                                        onClick={() => handleMarkAllAbsent(schedule)}
                                                        className="w-full rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 flex items-center justify-center gap-2"
                                                    >
                                                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                        </svg>
                                                        Mark All Absent
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        );
                        })}
                    </div>
                )}
            </div>

            {/* Attendance Details Modal */}
            {showModal && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="flex min-h-screen items-center justify-center px-4 py-4">
                        {/* Background overlay */}
                        <div
                            className="fixed inset-0 bg-gray-900 bg-opacity-75 transition-opacity"
                            aria-hidden="true"
                            onClick={closeModal}
                        ></div>

                        {/* Modal Content */}
                        <div className="relative z-50 w-full max-w-4xl transform overflow-hidden rounded-lg bg-white shadow-xl transition-all">
                            {/* Modal Header */}
                            <div className="border-b border-gray-200 bg-gradient-to-r from-indigo-50 to-blue-50 px-6 py-5">
                                <div className="flex items-start justify-between">
                                    <div className="flex items-center gap-4 flex-1">
                                        {/* Lecturer Image */}
                                        <div className="relative h-16 w-16 flex-shrink-0">
                                            {lecturerImageLoading ? (
                                                <div className="h-16 w-16 rounded-full bg-gray-200 flex items-center justify-center ring-4 ring-indigo-200">
                                                    <svg className="animate-spin h-8 w-8 text-indigo-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                    </svg>
                                                </div>
                                            ) : (
                                                <img
                                                    src={lecturerImage || 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 64 64"%3E%3Crect width="64" height="64" fill="%236366f1"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-family="Arial, sans-serif" font-size="28" fill="%23ffffff"%3E' + (selectedSchedule?.staffName?.charAt(0) || 'L') + '%3C/text%3E%3C/svg%3E'}
                                                    alt={selectedSchedule?.staffName || 'Lecturer'}
                                                    className="h-16 w-16 rounded-full object-cover ring-4 ring-indigo-200 shadow-md"
                                                    onError={(e) => {
                                                        const target = e.target;
                                                        target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 64 64"%3E%3Crect width="64" height="64" fill="%236366f1"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-family="Arial, sans-serif" font-size="28" fill="%23ffffff"%3E' + (selectedSchedule?.staffName?.charAt(0) || 'L') + '%3C/text%3E%3C/svg%3E';
                                                    }}
                                                />
                                            )}
                                        </div>
                                        
                                        {/* Lecturer and Subject Info */}
                                        <div className="flex-1 min-w-0">
                                            <h3 className="text-xl font-bold text-gray-900" id="modal-title">
                                                {selectedSchedule?.staffName || 'Lecturer'}
                                            </h3>
                                            {selectedSchedule && (
                                                <>
                                                    <p className="mt-1 text-sm font-medium text-indigo-700">
                                                        {selectedSchedule.subjectFullName || selectedSchedule.subjectShortName}
                                                        {selectedSchedule.subjectShortName && selectedSchedule.subjectFullName && 
                                                            selectedSchedule.subjectShortName !== selectedSchedule.subjectFullName && 
                                                            ` (${selectedSchedule.subjectShortName})`
                                                        }
                                                    </p>
                                                    <div className="mt-1 flex items-center gap-3 text-sm text-gray-600">
                                                        <span className="flex items-center gap-1">
                                                            <svg className="h-4 w-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                            </svg>
                                                            {to12h(selectedSchedule.startTime)} - {to12h(selectedSchedule.endTime)}
                                                        </span>
                                                        {selectedSchedule.classroomName && (
                                                            <span className="flex items-center gap-1">
                                                                <svg className="h-4 w-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                                                </svg>
                                                                {selectedSchedule.classroomName}
                                                            </span>
                                                        )}
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                    <button
                                        onClick={closeModal}
                                        className="ml-4 rounded-md text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                    >
                                        <span className="sr-only">Close</span>
                                        <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>
                                </div>
                            </div>

                            {/* Modal Body */}
                            <div className="max-h-[70vh] overflow-y-auto bg-gray-50 px-6 py-6">
                                {modalLoading ? (
                                    <div className="flex items-center justify-center py-12">
                                        <div className="h-12 w-12 animate-spin rounded-full border-4 border-indigo-600 border-t-transparent"></div>
                                    </div>
                                ) : attendanceDetails ? (
                                    <div className="space-y-6">
                                        {/* Search Input */}
                                        <div className="sticky top-0 z-10 rounded-lg bg-white p-4 shadow-sm border border-gray-200">
                                            <div className="flex items-center gap-3">
                                                <div className="flex-1 relative">
                                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                        <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                                        </svg>
                                                    </div>
                                                    <input
                                                        type="text"
                                                        placeholder="Search by student ID or name..."
                                                        value={searchQuery}
                                                        onChange={(e) => setSearchQuery(e.target.value)}
                                                        className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                                    />
                                                </div>
                                                {searchQuery && (
                                                    <button
                                                        onClick={() => setSearchQuery('')}
                                                        className="px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500"
                                                    >
                                                        Clear
                                                    </button>
                                                )}
                                            </div>
                                            {searchQuery && (
                                                <div className="mt-2 text-sm text-gray-600">
                                                    Showing results for: <span className="font-semibold text-indigo-600">"{searchQuery}"</span>
                                                </div>
                                            )}
                                        </div>

                                        {/* Summary Stats */}
                                        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                                            <div className="rounded-lg bg-white p-4 shadow-sm">
                                                <p className="text-sm text-gray-600">Total Students</p>
                                                <p className="mt-1 text-2xl font-bold text-gray-900">{attendanceDetails.summary.total}</p>
                                            </div>
                                            <div className="rounded-lg bg-green-50 p-4 shadow-sm">
                                                <p className="text-sm text-green-700">Present</p>
                                                <p className="mt-1 text-2xl font-bold text-green-900">{attendanceDetails.summary.present}</p>
                                            </div>
                                            <div className="rounded-lg bg-red-50 p-4 shadow-sm">
                                                <p className="text-sm text-red-700">Absent</p>
                                                <p className="mt-1 text-2xl font-bold text-red-900">{attendanceDetails.summary.absent}</p>
                                            </div>
                                            <div className="rounded-lg bg-blue-50 p-4 shadow-sm">
                                                <p className="text-sm text-blue-700">Attendance %</p>
                                                <p className="mt-1 text-2xl font-bold text-blue-900">{attendanceDetails.summary.percentage}%</p>
                                            </div>
                                        </div>

                                        {/* Batch-wise Summary for Batch Groups */}
                                        {attendanceDetails.isBatchGroup && attendanceDetails.batchWiseSummary && attendanceDetails.batchWiseSummary.length > 0 && (
                                            <div className="rounded-lg bg-white p-4 shadow-sm">
                                                <h4 className="mb-4 text-base font-semibold text-gray-900">
                                                    Batch-wise Attendance Summary
                                                </h4>
                                                <div className="space-y-4">
                                                    {attendanceDetails.batchWiseSummary.map((batch, idx) => (
                                                        <div key={idx} className="border-l-4 border-indigo-500 bg-indigo-50 p-4 rounded-r-lg">
                                                            <div className="flex items-center justify-between mb-2">
                                                                <h5 className="font-semibold text-gray-900">{batch.batchName}</h5>
                                                                <span className="text-lg font-bold text-indigo-700">{batch.percentage}%</span>
                                                            </div>
                                                            
                                                            {/* Progress Bar */}
                                                            <div className="mb-3">
                                                                <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200">
                                                                    <div
                                                                        className={`h-full rounded-full transition-all ${
                                                                            batch.percentage >= 75
                                                                                ? 'bg-green-500'
                                                                                : batch.percentage >= 50
                                                                                ? 'bg-yellow-500'
                                                                                : 'bg-red-500'
                                                                        }`}
                                                                        style={{ width: `${batch.percentage}%` }}
                                                                    ></div>
                                                                </div>
                                                            </div>
                                                            
                                                            {/* Stats Grid */}
                                                            <div className="grid grid-cols-2 gap-2 text-xs sm:grid-cols-5">
                                                                <div className="flex items-center justify-between rounded-md bg-white px-2 py-1.5 border border-gray-200">
                                                                    <span className="text-gray-700">Total</span>
                                                                    <span className="font-semibold text-gray-900">{batch.total}</span>
                                                                </div>
                                                                <div className="flex items-center justify-between rounded-md bg-green-50 px-2 py-1.5 border border-green-200">
                                                                    <span className="text-green-700">Present</span>
                                                                    <span className="font-semibold text-green-900">{batch.present}</span>
                                                                </div>
                                                                <div className="flex items-center justify-between rounded-md bg-red-50 px-2 py-1.5 border border-red-200">
                                                                    <span className="text-red-700">Absent</span>
                                                                    <span className="font-semibold text-red-900">{batch.absent}</span>
                                                                </div>
                                                                {batch.late > 0 && (
                                                                    <div className="flex items-center justify-between rounded-md bg-yellow-50 px-2 py-1.5 border border-yellow-200">
                                                                        <span className="text-yellow-700">Late</span>
                                                                        <span className="font-semibold text-yellow-900">{batch.late}</span>
                                                                    </div>
                                                                )}
                                                                {batch.excused > 0 && (
                                                                    <div className="flex items-center justify-between rounded-md bg-blue-50 px-2 py-1.5 border border-blue-200">
                                                                        <span className="text-blue-700">Excused</span>
                                                                        <span className="font-semibold text-blue-900">{batch.excused}</span>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {/* Present Students */}
                                        {attendanceDetails.present && attendanceDetails.present.length > 0 && (
                                            <div className="rounded-lg bg-white p-4 shadow-sm">
                                                <h4 className="mb-3 flex items-center gap-2 text-base font-semibold text-gray-900">
                                                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-green-100">
                                                        <svg className="h-4 w-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                        </svg>
                                                    </span>
                                                    Present Students ({filterStudents(attendanceDetails.present).length}{searchQuery && ` of ${attendanceDetails.present.length}`})
                                                </h4>
                                                <div className="max-h-96 overflow-y-auto">
                                                    {filterStudents(attendanceDetails.present).length > 0 ? (
                                                        <div className="space-y-3">
                                                            {filterStudents(attendanceDetails.present).map((student, idx) => (
                                                            <div key={idx} className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 p-4 hover:bg-gray-100">
                                                                <div className="flex items-center gap-4">
                                                                    {/* Student Image */}
                                                                    <div className="relative h-12 w-12 flex-shrink-0">
                                                                        {loadingImages.has(student.studentId) ? (
                                                                            <div className="h-12 w-12 rounded-full bg-gray-200 flex items-center justify-center ring-2 ring-green-500">
                                                                                <svg className="animate-spin h-6 w-6 text-green-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                                                </svg>
                                                                            </div>
                                                                        ) : (
                                                                            <img
                                                                                src={studentImages[student.studentId] || 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48"%3E%3Crect width="48" height="48" fill="%23e5e7eb"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-family="Arial, sans-serif" font-size="20" fill="%236b7280"%3E' + (student.studentName?.charAt(0) || '?') + '%3C/text%3E%3C/svg%3E'}
                                                                                alt={student.studentName}
                                                                                className="h-12 w-12 rounded-full object-cover ring-2 ring-green-500"
                                                                            />
                                                                        )}
                                                                    </div>
                                                                    <div className="flex-1">
                                                                        <p className="font-medium text-gray-900">{student.studentName || 'N/A'}</p>
                                                                        <p className="text-sm text-gray-600">{student.studentId}</p>
                                                                        {student.mobileNumber && (
                                                                            <p className="text-sm text-gray-500">📱 {student.mobileNumber}</p>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                                <div className="flex items-center gap-3">
                                                                    <span className="inline-flex rounded-full bg-green-100 px-3 py-1 text-xs font-semibold text-green-800">
                                                                        {student.status}
                                                                    </span>
                                                                    <button
                                                                        onClick={() => toggleAttendanceStatus(student.attendanceId, student.status)}
                                                                        className="rounded-md bg-red-600 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
                                                                    >
                                                                        Mark Absent
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <div className="text-center py-8 text-gray-500">
                                                        No students found matching "{searchQuery}"
                                                    </div>
                                                )}
                                                </div>
                                            </div>
                                        )}

                                        {/* Absent Students */}
                                        {attendanceDetails.absent && attendanceDetails.absent.length > 0 && (
                                            <div className="rounded-lg bg-white p-4 shadow-sm">
                                                <h4 className="mb-3 flex items-center gap-2 text-base font-semibold text-gray-900">
                                                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-red-100">
                                                        <svg className="h-4 w-4 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                                                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                                        </svg>
                                                    </span>
                                                    Absent Students ({filterStudents(attendanceDetails.absent).length}{searchQuery && ` of ${attendanceDetails.absent.length}`})
                                                </h4>
                                                <div className="max-h-96 overflow-y-auto">
                                                    {filterStudents(attendanceDetails.absent).length > 0 ? (
                                                        <div className="space-y-3">
                                                            {filterStudents(attendanceDetails.absent).map((student, idx) => (
                                                            <div key={idx} className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 p-4 hover:bg-gray-100">
                                                                <div className="flex items-center gap-4">
                                                                    {/* Student Image */}
                                                                    <div className="relative h-12 w-12 flex-shrink-0">
                                                                        {loadingImages.has(student.studentId) ? (
                                                                            <div className="h-12 w-12 rounded-full bg-gray-200 flex items-center justify-center ring-2 ring-red-500">
                                                                                <svg className="animate-spin h-6 w-6 text-red-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                                                </svg>
                                                                            </div>
                                                                        ) : (
                                                                            <img
                                                                                src={studentImages[student.studentId] || 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48"%3E%3Crect width="48" height="48" fill="%23e5e7eb"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-family="Arial, sans-serif" font-size="20" fill="%236b7280"%3E' + (student.studentName?.charAt(0) || '?') + '%3C/text%3E%3C/svg%3E'}
                                                                                alt={student.studentName}
                                                                                className="h-12 w-12 rounded-full object-cover ring-2 ring-red-500"
                                                                            />
                                                                        )}
                                                                    </div>
                                                                    <div className="flex-1">
                                                                        <p className="font-medium text-gray-900">{student.studentName || 'N/A'}</p>
                                                                        <p className="text-sm text-gray-600">{student.studentId}</p>
                                                                        {student.mobileNumber && (
                                                                            <p className="text-sm text-gray-500">📱 {student.mobileNumber}</p>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                                <div className="flex items-center gap-3">
                                                                    <span className="inline-flex rounded-full bg-red-100 px-3 py-1 text-xs font-semibold text-red-800">
                                                                        {student.status}
                                                                    </span>
                                                                    <button
                                                                        onClick={() => toggleAttendanceStatus(student.attendanceId, student.status)}
                                                                        className="rounded-md bg-green-600 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
                                                                    >
                                                                        Mark Present
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <div className="text-center py-8 text-gray-500">
                                                        No students found matching "{searchQuery}"
                                                    </div>
                                                )}
                                                </div>
                                            </div>
                                        )}

                                        {/* Late Students */}
                                        {attendanceDetails.late && attendanceDetails.late.length > 0 && (
                                            <div className="rounded-lg bg-white p-4 shadow-sm">
                                                <h4 className="mb-3 flex items-center gap-2 text-base font-semibold text-gray-900">
                                                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-yellow-100">
                                                        <svg className="h-4 w-4 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
                                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                                                        </svg>
                                                    </span>
                                                    Late Students ({filterStudents(attendanceDetails.late).length}{searchQuery && ` of ${attendanceDetails.late.length}`})
                                                </h4>
                                                <div className="max-h-96 overflow-y-auto">
                                                    {filterStudents(attendanceDetails.late).length > 0 ? (
                                                        <div className="space-y-3">
                                                            {filterStudents(attendanceDetails.late).map((student, idx) => (
                                                            <div key={idx} className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 p-4 hover:bg-gray-100">
                                                                <div className="flex items-center gap-4">
                                                                    <div className="relative h-12 w-12 flex-shrink-0">
                                                                        {loadingImages.has(student.studentId) ? (
                                                                            <div className="h-12 w-12 rounded-full bg-gray-200 flex items-center justify-center ring-2 ring-yellow-500">
                                                                                <svg className="animate-spin h-6 w-6 text-yellow-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                                                </svg>
                                                                            </div>
                                                                        ) : (
                                                                            <img
                                                                                src={studentImages[student.studentId] || 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48"%3E%3Crect width="48" height="48" fill="%23e5e7eb"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-family="Arial, sans-serif" font-size="20" fill="%236b7280"%3E' + (student.studentName?.charAt(0) || '?') + '%3C/text%3E%3C/svg%3E'}
                                                                                alt={student.studentName}
                                                                                className="h-12 w-12 rounded-full object-cover ring-2 ring-yellow-500"
                                                                            />
                                                                        )}
                                                                    </div>
                                                                    <div className="flex-1">
                                                                        <p className="font-medium text-gray-900">{student.studentName || 'N/A'}</p>
                                                                        <p className="text-sm text-gray-600">{student.studentId}</p>
                                                                        {student.mobileNumber && (
                                                                            <p className="text-sm text-gray-500">📱 {student.mobileNumber}</p>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                                <span className="inline-flex rounded-full bg-yellow-100 px-3 py-1 text-xs font-semibold text-yellow-800">
                                                                    {student.status}
                                                                </span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <div className="text-center py-8 text-gray-500">
                                                        No students found matching "{searchQuery}"
                                                    </div>
                                                )}
                                                </div>
                                            </div>
                                        )}

                                        {/* Excused Students */}
                                        {attendanceDetails.excused && attendanceDetails.excused.length > 0 && (
                                            <div className="rounded-lg bg-white p-4 shadow-sm">
                                                <h4 className="mb-3 flex items-center gap-2 text-base font-semibold text-gray-900">
                                                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-100">
                                                        <svg className="h-4 w-4 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                                                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                                        </svg>
                                                    </span>
                                                    Excused Students ({filterStudents(attendanceDetails.excused).length}{searchQuery && ` of ${attendanceDetails.excused.length}`})
                                                </h4>
                                                <div className="max-h-96 overflow-y-auto">
                                                    {filterStudents(attendanceDetails.excused).length > 0 ? (
                                                        <div className="space-y-3">
                                                            {filterStudents(attendanceDetails.excused).map((student, idx) => (
                                                            <div key={idx} className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 p-4 hover:bg-gray-100">
                                                                <div className="flex items-center gap-4">
                                                                    <div className="relative h-12 w-12 flex-shrink-0">
                                                                        {loadingImages.has(student.studentId) ? (
                                                                            <div className="h-12 w-12 rounded-full bg-gray-200 flex items-center justify-center ring-2 ring-blue-500">
                                                                                <svg className="animate-spin h-6 w-6 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                                                </svg>
                                                                            </div>
                                                                        ) : (
                                                                            <img
                                                                                src={studentImages[student.studentId] || 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48"%3E%3Crect width="48" height="48" fill="%23e5e7eb"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-family="Arial, sans-serif" font-size="20" fill="%236b7280"%3E' + (student.studentName?.charAt(0) || '?') + '%3C/text%3E%3C/svg%3E'}
                                                                                alt={student.studentName}
                                                                                className="h-12 w-12 rounded-full object-cover ring-2 ring-blue-500"
                                                                            />
                                                                        )}
                                                                    </div>
                                                                    <div className="flex-1">
                                                                        <p className="font-medium text-gray-900">{student.studentName || 'N/A'}</p>
                                                                        <p className="text-sm text-gray-600">{student.studentId}</p>
                                                                        {student.mobileNumber && (
                                                                            <p className="text-sm text-gray-500">📱 {student.mobileNumber}</p>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                                <span className="inline-flex rounded-full bg-blue-100 px-3 py-1 text-xs font-semibold text-blue-800">
                                                                    {student.status}
                                                                </span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <div className="text-center py-8 text-gray-500">
                                                        No students found matching "{searchQuery}"
                                                    </div>
                                                )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <p className="text-center text-gray-500">No attendance data available</p>
                                )}
                            </div>

                            {/* Modal Footer */}
                            <div className="border-t border-gray-200 bg-white px-6 py-4">
                                <button
                                    onClick={closeModal}
                                    className="w-full rounded-md bg-gray-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 sm:w-auto"
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </DashboardLayout>
    );
}

